/**
 * The coordinator's side.
 *
 * Query keys are namespaced by vertical, because a coordinator with two grants
 * switching between them must not see the first one's list while the second
 * loads — a screen full of the wrong vertical's applicants is worse than a
 * blank one.
 */
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api } from "./api";
import { messageOf } from "./errors";

export type StateFilter =
  | "all"
  | "in_progress"
  | "active"
  | "rejected"
  | "selected"
  | "withdrawn";

export type Outcome = "pending" | "advanced" | "rejected" | "selected";

async function fetchOverview(vertical: string) {
  const { data, error } = await api.coord({ vertical }).get();
  if (error) throw new Error(messageOf(error.value, "You do not coordinate this vertical."));
  return data;
}

export const coordQuery = (vertical: string) =>
  queryOptions({
    queryKey: ["coord", vertical],
    queryFn: () => fetchOverview(vertical),
  });

async function fetchApplicants(vertical: string, state: StateFilter) {
  const { data, error } = await api
    .coord({ vertical })
    .applicants.get({ query: { state } });
  if (error) throw new Error(messageOf(error.value, "Could not load the applicants."));
  return data;
}

export const applicantsQuery = (vertical: string, state: StateFilter) =>
  queryOptions({
    queryKey: ["coord", vertical, "applicants", state],
    queryFn: () => fetchApplicants(vertical, state),
  });

export type Applicants = Awaited<ReturnType<typeof fetchApplicants>>;
export type ApplicantRow = Applicants["items"][number];

async function fetchApplicant(vertical: string, id: string) {
  const { data, error } = await api.coord({ vertical }).applicants({ id }).get();
  if (error) throw new Error(messageOf(error.value, "Could not load that applicant."));
  return data;
}

export const applicantQuery = (vertical: string, id: string) =>
  queryOptions({
    queryKey: ["coord", vertical, "applicant", id],
    queryFn: () => fetchApplicant(vertical, id),
  });

export type Applicant = Awaited<ReturnType<typeof fetchApplicant>>;

async function fetchRounds(vertical: string) {
  const { data, error } = await api.coord({ vertical }).rounds.get();
  if (error) throw new Error(messageOf(error.value, "Could not load the rounds."));
  return data;
}

export const roundsQuery = (vertical: string) =>
  queryOptions({
    queryKey: ["coord", vertical, "rounds"],
    queryFn: () => fetchRounds(vertical),
  });

export type BuilderRounds = Awaited<ReturnType<typeof fetchRounds>>;
export type BuilderRound = BuilderRounds["rounds"][number];
export type BuilderQuestion = NonNullable<BuilderRound["form"]>["questions"][number];

/** Everything under this vertical: lists, detail, rounds, the overview. */
function invalidateVertical(qc: QueryClient, vertical: string) {
  return qc.invalidateQueries({ queryKey: ["coord", vertical] });
}

export function useDecide(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      applicationIds: string[];
      roundId: string;
      outcome: Outcome;
      note?: string;
    }) => {
      const { data, error } = await api.coord({ vertical }).results.post(input);
      if (error) throw new Error(messageOf(error.value, "Could not record that decision."));
      return data;
    },
    onSuccess: () => invalidateVertical(qc, vertical),
  });
}

export function useAddNote(vertical: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const { data, error } = await api
        .coord({ vertical })
        .applicants({ id })
        .notes.post({ body });
      if (error) throw new Error(messageOf(error.value, "Could not save that note."));
      return data;
    },
    onSuccess: () => invalidateVertical(qc, vertical),
  });
}

export function useCreateRound(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      kind: "application" | "task" | "interview" | "group" | "other";
      name: string;
      instructions?: string;
      opensAt?: string | null;
      closesAt?: string | null;
    }) => {
      const { data, error } = await api.coord({ vertical }).rounds.post(input);
      if (error) throw new Error(messageOf(error.value, "Could not add that round."));
      return data;
    },
    onSuccess: () => invalidateVertical(qc, vertical),
  });
}

export function useUpdateRound(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      roundId,
      ...patch
    }: {
      roundId: string;
      name?: string;
      instructions?: string;
      opensAt?: string | null;
      closesAt?: string | null;
    }) => {
      const { data, error } = await api.coord({ vertical }).rounds({ roundId }).patch(patch);
      if (error) throw new Error(messageOf(error.value, "Could not save that round."));
      return data;
    },
    onSuccess: () => invalidateVertical(qc, vertical),
  });
}

export function useDeleteRound(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roundId: string) => {
      const { data, error } = await api.coord({ vertical }).rounds({ roundId }).delete();
      if (error) throw new Error(messageOf(error.value, "Could not delete that round."));
      return data;
    },
    onSuccess: () => invalidateVertical(qc, vertical),
  });
}

export function useAddQuestion(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      roundId,
      ...question
    }: {
      roundId: string;
      kind: "short" | "long" | "select" | "multiselect" | "url" | "boolean";
      label: string;
      help?: string;
      required?: boolean;
      options?: string[];
    }) => {
      const { data, error } = await api
        .coord({ vertical })
        .rounds({ roundId })
        .questions.post(question);
      if (error) throw new Error(messageOf(error.value, "Could not add that question."));
      return data;
    },
    onSuccess: () => invalidateVertical(qc, vertical),
  });
}

export function useUpdateQuestion(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      roundId,
      questionId,
      ...patch
    }: {
      roundId: string;
      questionId: string;
      label?: string;
      help?: string;
      required?: boolean;
      archived?: boolean;
      position?: number;
      options?: string[];
    }) => {
      const { data, error } = await api
        .coord({ vertical })
        .rounds({ roundId })
        .questions({ questionId })
        .patch(patch);
      if (error) throw new Error(messageOf(error.value, "Could not save that question."));
      return data;
    },
    onSuccess: () => invalidateVertical(qc, vertical),
  });
}

/**
 * Attach a file to a round.
 *
 * Posted as multipart through Treaty rather than as a base64 blob in JSON:
 * the server reads the bytes and decides what the file IS from them, and a
 * 10MB attachment re-encoded as a string is 13MB of request body plus a
 * decode on both ends for nothing.
 */
export function useAttachFile(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roundId, file }: { roundId: string; file: File }) => {
      const { data, error } = await api
        .coord({ vertical })
        .rounds({ roundId })
        .files.post({ file });
      if (error) throw new Error(messageOf(error.value, "Could not attach that file."));
      return data;
    },
    onSuccess: () => invalidateVertical(qc, vertical),
  });
}

export function useRemoveFile(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roundId, fileId }: { roundId: string; fileId: string }) => {
      const { data, error } = await api
        .coord({ vertical })
        .rounds({ roundId })
        .files({ fileId })
        .delete();
      if (error) throw new Error(messageOf(error.value, "Could not remove that file."));
      return data;
    },
    onSuccess: () => invalidateVertical(qc, vertical),
  });
}
